import { ensureAuthenticated } from '@lune/auth/ensure'
import { ErrorPage } from '@lune/common/errors'
import { printRouterTree } from '@lune/common/router'
import React from 'react'
import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom'
import { version } from './api/version'
import { Dashboard } from './components/dashboard'
import { needsWhatsNew } from './pages/whatsNewHelper'
import {
  BackupPrintPage,
  BackupRoute,
  DefaultPage,
  LegacyFancyLoginPage,
  LegacyLoginFailed,
  LegacyLoginSuccess,
  LoginFailedPage,
  LoginPage,
  LoginSuccessPage,
  NewItemRoute,
  NotesRoute,
  OTPsRoute,
  PasswordsRoute,
  Root,
  WhatsNewPageFromLogin,
  WhatsNewRoute
} from './paths'

const OTPs = React.lazy(
  async () =>
    await import('./pages/otps').then((module) => ({
      default: module.OtpsPage
    }))
)
const OTPsNew = React.lazy(
  async () =>
    await import('./pages/otpsNew').then((module) => ({
      default: module.NewOtpPage
    }))
)
const Passwords = React.lazy(
  async () =>
    await import('./pages/passwords').then((module) => ({
      default: module.PasswordsPage
    }))
)
const PasswordsNew = React.lazy(
  async () =>
    await import('./pages/passwordsNew').then((module) => ({
      default: module.NewPasswordPage
    }))
)
const Notes = React.lazy(
  async () =>
    await import('./pages/notes').then((module) => ({
      default: module.NotesPage
    }))
)
const Backup = React.lazy(
  async () =>
    await import('./pages/backup').then((module) => ({
      default: module.BackupPage
    }))
)
const BackupPrint = React.lazy(
  async () =>
    await import('./pages/backupPrint').then((module) => ({
      default: module.BackupPrintPage
    }))
)
const NotesNew = React.lazy(
  async () =>
    await import('./pages/notesNew').then((module) => ({
      default: module.NewNotePage
    }))
)
const PageLogin = React.lazy(
  async () =>
    await import('./pages/login').then((module) => ({
      default: module.LoginPage
    }))
)
const WhatsNew = React.lazy(
  async () =>
    await import('./pages/whatsNew').then((module) => ({
      default: module.WhatsNewPage
    }))
)

const routerPaths = [
  {
    path: LoginSuccessPage.login.success.router.toString(),
    element: (
      <Navigate
        to={needsWhatsNew() ? WhatsNewPageFromLogin : DefaultPage}
        replace={true}
      />
    ),
    errorElement: <ErrorPage />
  },
  {
    path: LoginPage.login.url.toString(),
    element: <PageLogin />,
    errorElement: <ErrorPage />
  },
  {
    path: LegacyFancyLoginPage,
    element: <Navigate to={LoginPage.login.url.toString()} replace={true} />,
    errorElement: <ErrorPage />
  },
  {
    path: LegacyLoginSuccess,
    element: (
      <Navigate
        to={LoginSuccessPage.login.success.url.toString()}
        replace={true}
      />
    ),
    errorElement: <ErrorPage />
  },
  {
    path: LegacyLoginFailed,
    element: (
      <Navigate
        to={LoginFailedPage.login.failed.url.toString()}
        replace={true}
      />
    ),
    errorElement: <ErrorPage />
  },
  {
    path: BackupPrintPage,
    element: <BackupPrint />,
    errorElement: <ErrorPage />
  },
  {
    path: Root.root.url.toString(),
    element: <Dashboard />,
    loader: async () => await ensureAuthenticated(version, LoginPage, Root),
    errorElement: <ErrorPage />,
    children: [
      {
        path: Root.root.url.toString(),
        element: <Navigate to={DefaultPage} replace={true} />,
        errorElement: <ErrorPage />
      },
      {
        path: OTPsRoute,
        element: <OTPs />,
        errorElement: <ErrorPage />
      },
      {
        path: PasswordsRoute,
        element: <Passwords />,
        errorElement: <ErrorPage />
      },
      {
        path: NotesRoute,
        element: <Notes />,
        errorElement: <ErrorPage />
      },
      {
        path: BackupRoute,
        element: <Backup />,
        errorElement: <ErrorPage />
      },
      {
        path: NewItemRoute,
        errorElement: <ErrorPage />,
        children: [
          {
            path: OTPsRoute,
            element: <OTPsNew />,
            errorElement: <ErrorPage />
          },
          {
            path: PasswordsRoute,
            element: <PasswordsNew />,
            errorElement: <ErrorPage />
          },
          {
            path: NotesRoute,
            element: <NotesNew />,
            errorElement: <ErrorPage />
          }
        ]
      },
      {
        path: WhatsNewRoute,
        element: <WhatsNew />,
        errorElement: <ErrorPage />
      }
    ]
  }
]

printRouterTree(routerPaths)

const router = createBrowserRouter(routerPaths)

export const App: React.FC = () => <RouterProvider router={router} />
